import { useEffect, useRef } from "react";
import { ChangeAlertCallbackType, ChangeLoaderCallbackType } from "../../../Root";
import { LeadService } from "../../../../services/LeadService";
import { ManualCheckPayload } from "../pldStatus";

export type UseBlacklistCruiseOptions = {
    changeAlert: ChangeAlertCallbackType;
    changeLoader?: ChangeLoaderCallbackType;
    leadUuid: string;
    onSearch?: (sectionName: string) => void;
};

/**
 * Encapsula las acciones del cruce PLD contra el backend:
 * guardar el resultado del cruce manual y reintentar el cruce.
 */
export const useBlacklistCruise = ({ changeAlert, changeLoader, leadUuid, onSearch }: UseBlacklistCruiseOptions) => {
    const leadService = useRef<LeadService>();

    useEffect(() => {
        leadService.current = new LeadService(changeAlert);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Guarda el resultado del cruce manual (aprobado/rechazado) y refresca la sección.
     */
    const saveManualCheck = async (payload: ManualCheckPayload): Promise<boolean> => {
        changeLoader?.(true);
        try {
            const resp = await leadService.current?.pldManualCheck(payload);

            if (resp?.data?.response !== "OK") {
                changeAlert(true, "error", resp?.data?.message || "Error al registrar el cruce manual.");
                return false;
            }

            const approved = payload.manualcheck.status === "approved";
            changeAlert(
                true,
                approved ? "success" : "error",
                approved
                    ? "Cruce manual registrado: cruce aprobado. El cruce queda aprobado. Ya puedes avanzar al siguiente estatus: Contrato enviado"
                    : "Cruce manual registrado: cruce NO aprobado. La cuenta no puede abrirse. Se generó alerta y evidencia; 'Contrato enviado' permanece bloqueado."
            );
            onSearch?.("COMPANY_INFO");
            return true;
        } catch {
            changeAlert(true, "error", "Error inesperado al registrar el cruce manual.");
            return false;
        } finally {
            changeLoader?.(false);
        }
    };

    /**
     * Reintenta el cruce solo para las validaciones externas que fallaron y refresca la sección.
     */
    const retryCruise = async (externalValidations: Array<any> = []): Promise<boolean> => {
        if (!leadUuid) {
            changeAlert(true, "error", "No se encontró el UUID del expediente para reintentar el cruce.");
            return false;
        }

        changeLoader?.(true);
        try {
            const resp = await leadService.current?.pldRetry({ leadUuid: leadUuid, external_validations: externalValidations });

            // El service devuelve una respuesta por cada validación reintentada en content.results[]
            const results: any[] = resp?.content?.results ?? [];
            const hasFailure = results.some((item: any) => item?.request?.response !== "OK");

            if (resp?.response !== "OK" || hasFailure) {
                changeAlert(true, "error", resp?.message || "Error al reintentar el cruce PLD. Una o más validaciones no se pudieron reintentar.");
                return false;
            }

            changeAlert(true, "info", "Cruce PLD reiniciado. Actualizando datos de la sección...");
            onSearch?.("COMPANY_INFO");
            return true;
        } catch {
            changeAlert(true, "error", "Error inesperado al reintentar el cruce PLD.");
            return false;
        } finally {
            changeLoader?.(false);
        }
    };

    /**
     * Genera el cruce de listas restringidas y blacklist interna desde cero
     * (solo cuando no existe ninguna validación previa) y refresca la sección.
     */
    const generateCruise = async (): Promise<boolean> => {
        if (!leadUuid) {
            changeAlert(true, "error", "No se encontró el UUID del expediente para generar el cruce.");
            return false;
        }

        changeLoader?.(true);
        try {
            const resp = await leadService.current?.startBlacklistValidation({ leadUuid });

            if (resp?.data?.response !== "OK") {
                changeAlert(true, "error", resp?.data?.message || resp?.message || "Error al generar el cruce PLD.");
                return false;
            }

            changeAlert(true, "info", "Cruce PLD generado. Actualizando datos de la sección...");
            onSearch?.("COMPANY_INFO");
            return true;
        } catch {
            changeAlert(true, "error", "Error inesperado al generar el cruce PLD.");
            return false;
        } finally {
            changeLoader?.(false);
        }
    };

    return { saveManualCheck, retryCruise, generateCruise };
};
